/**
 * Sub-componente reutilizable que renderiza una de las 3 secciones del
 * ColumnConfigModal (Core / Native / Tags).
 *
 * Spec: docs/architecture/COLUMN_CONFIG_UI_SPEC.md
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GripVertical,
  Trash2,
  Plus,
  Sparkles,
  Eye,
  Loader2,
  X as XIcon,
} from 'lucide-react';
import { VALID_CATALOG_FIELDS } from '../cells/MergedTomaCatalogCell';
import { getSampleValues } from '../../services/columnConfig';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  formatValue,
  ColumnFormat,
  FormatType,
} from '../../utils/columnFormatters';
import {
  FORMAT_PRESETS,
  findClosestPreset,
  presetToFormat,
} from '../../utils/columnFormatPresets';
import { COLUMN_STYLE_PRESETS } from '../../utils/columnStylePresets';
import type {
  NativeCandidate,
  TagCandidate,
} from '../../services/columnConfig';

// Columnas Core que NUNCA deben tener mergeWithCatalog (cell renderer dedicado,
// no admiten merge silencioso con catalog). Guardrail post-incidente Adidas 2026-05-23:
// alguien seteó core.imagen.mergeWithCatalog=catalog_brand y la celda IMAGEN dejó de
// mostrar imágenes para mostrar la marca con tooltip "Origen: Cat".
const NON_MERGEABLE_CORE_IDS = new Set([
  'imagen',         // renderer dedicado <img>
  'titulo',         // renderer dedicado (titulo + URL link)
  'buybox',         // indicador especial
  'precio',         // numérico — catalog no tiene equivalente
  'precio_desc',    // numérico
  'precio_venta',   // numérico
  'cuotas',         // numérico
  'precio_cuota',   // numérico
  'pagina_orden',   // composite (page + orderInPage)
  'actions',        // virtual (ya excluida por isSystem)
  'selection',      // virtual (ya excluida por isSystem)
]);

// ----- Tipos -----

export type SectionType = 'core' | 'native' | 'tags';

export interface SectionEntry {
  id: string;
  enabled?: boolean;
  displayName?: string;
  description?: string;
  order?: number;
  sourceField?: string;
  format?: FormatType;
  decimals?: number;
  thousandsSeparator?: boolean;
  currencySymbol?: string;
  pendingReview?: boolean;
  sample?: unknown;
  styleId?: string;
  // Merge silencioso toma↔catalog (Core + Native).
  // Si está seteado, la columna existente se renderiza con MergedTomaCatalogCell.
  // Whitelist en VALID_CATALOG_FIELDS (catalog_brand|catalog_category|subcategory|segmento|contenido|udm).
  mergeWithCatalog?: string | null;
  // Inline edit (Core + Native). Solo aplica si el resolved editable field
  // está en EDITABLE_WHITELIST de catalogPatch (server-side). Ver columnConfig.ts.
  editable?: boolean;
}

interface Props {
  sectionType: SectionType;
  entries: SectionEntry[];
  candidates?: (NativeCandidate | TagCandidate)[]; // solo Native/Tags
  clientId?: string; // necesario para sample-values lookup
  onChange: (id: string, updates: Partial<SectionEntry>) => void;
  onReorder: (orderedIds: string[]) => void;
  onAdd?: (id: string, defaultEntry: Partial<SectionEntry>) => void;
  onRemove?: (id: string) => void;
}

// ----- Helpers de fallback de sample para preview -----

const FALLBACK_SAMPLE: Record<FormatType, unknown> = {
  text: 'Sample',
  number: 12345.67,
  percentage: 25,
  currency: 12345.67,
  date: '2026-05-04',
  boolean: true,
};

function pickSample(entry: SectionEntry): unknown {
  if (entry.sample !== undefined && entry.sample !== null && entry.sample !== '') {
    return entry.sample;
  }
  return FALLBACK_SAMPLE[entry.format || 'text'];
}

function entryToFormat(entry: SectionEntry): ColumnFormat {
  return {
    format: entry.format || 'text',
    decimals: entry.decimals,
    thousandsSeparator: entry.thousandsSeparator,
    currencySymbol: entry.currencySymbol,
  };
}

// Mapeo Core → field real en la base. Las Core columnas virtuales (selection,
// actions, buybox, pagina_orden) no aparecen acá → no se les ofrece "Consultar".
const CORE_TO_DB_FIELD: Record<string, string> = {
  marca: 'Marca',
  categoria: 'Cat',
  titulo: 'title',
  imagen: 'imagen',
  sitio: 'sitio',
  precio: 'original_price',
  precio_desc: 'discounted_price',
  precio_venta: 'sale_price',
  cuotas: 'installements',
  precio_cuota: 'installements_value',
  seller: 'Seller',
};

// ----- Fila individual con drag-drop -----

interface RowProps {
  entry: SectionEntry;
  sectionType: SectionType;
  clientId?: string;
  onChange: (updates: Partial<SectionEntry>) => void;
  onRemove?: () => void;
}

const ColumnRow: React.FC<RowProps> = ({ entry, sectionType, clientId, onChange, onRemove }) => {
  const { t } = useTranslation();
  // Popover de valores reales (sample-values endpoint, on-demand).
  const [showValuesPopover, setShowValuesPopover] = useState(false);
  const [valuesData, setValuesData] = useState<{
    values: { value: unknown; count: number }[];
    totalDistinct: number;
    hasToma: boolean;
  } | null>(null);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [valuesError, setValuesError] = useState<string | null>(null);

  const handleOpenValues = async () => {
    setShowValuesPopover(true);
    if (valuesData || valuesLoading || !clientId) return;
    setValuesLoading(true);
    setValuesError(null);
    try {
      const tp = sectionType === 'tags' ? 'tags' : 'native';
      const fld = sectionType === 'tags'
        ? entry.id
        : sectionType === 'core'
          ? (CORE_TO_DB_FIELD[entry.id] || entry.id)
          : (entry.sourceField || entry.id);
      const res = await getSampleValues(clientId, fld, tp, 20);
      setValuesData({
        values: res.values || [],
        totalDistinct: res.totalDistinct,
        hasToma: res.hasToma,
      });
    } catch (e: any) {
      setValuesError(e?.response?.data?.error || e?.message || 'Error al cargar valores');
    } finally {
      setValuesLoading(false);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${sectionType}-${entry.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const showFormat = sectionType !== 'core';
  const showRemove = sectionType !== 'core' && !!onRemove;
  const isSystem = entry.id === 'actions' || entry.id === 'selection';

  const currentPresetId = useMemo(
    () => findClosestPreset(entryToFormat(entry)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entry.format, entry.decimals, entry.thousandsSeparator, entry.currencySymbol]
  );

  const handlePresetChange = (presetId: string) => {
    const fmt = presetToFormat(presetId);
    onChange({
      format: fmt.format,
      decimals: fmt.decimals,
      thousandsSeparator: fmt.thousandsSeparator,
      currencySymbol: fmt.currencySymbol,
    });
  };

  const previewText = showFormat
    ? formatValue(pickSample(entry), entryToFormat(entry))
    : null;

  // Color de border-l-4 según estado
  const borderColor = isDragging
    ? 'border-pink-400'
    : entry.pendingReview
    ? 'border-orange-400'
    : entry.enabled === false
    ? 'border-gray-300'
    : 'border-green-500';

  const bgClass = entry.enabled === false ? 'bg-gray-50' : 'bg-white';

  const canFetchValues = !!clientId && !isSystem && (
    sectionType === 'core' ? !!CORE_TO_DB_FIELD[entry.id] : true
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative border ${bgClass} border-gray-200 rounded-lg border-l-4 ${borderColor} p-3 ${
        entry.enabled === false ? 'opacity-70' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle */}
        <button
          type="button"
          className="mt-1 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
          {...attributes}
          {...listeners}
          title={t('admin.columnConfig.actions.dragToReorder', 'Arrastrá para reordenar')}
          disabled={isSystem}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Checkbox enabled */}
        {!isSystem ? (
          <input
            type="checkbox"
            checked={entry.enabled !== false}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="mt-1 w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
            title={t('admin.columnConfig.fields.toggleActive', 'Activar / Desactivar')}
          />
        ) : (
          <span className="mt-1 w-4 h-4 inline-block" />
        )}

        {/* Cuerpo */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Línea 1: id + badges + acciones */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {entry.id}
              </span>
              {sectionType === 'native' && (
                <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded uppercase tracking-wide">
                  {t('admin.columnConfig.badges.native', 'Nativa')}
                </span>
              )}
              {sectionType === 'tags' && (
                <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 rounded uppercase tracking-wide">
                  {t('admin.columnConfig.badges.tag', 'Tag')}
                </span>
              )}
              {entry.pendingReview && (
                <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 rounded uppercase tracking-wide">
                  {t('admin.columnConfig.badges.pendingReview', 'Nueva')}
                </span>
              )}
              {isSystem && (
                <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded uppercase tracking-wide">
                  {t('admin.columnConfig.badges.system', 'Sistema')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canFetchValues && (
                <button
                  type="button"
                  onClick={handleOpenValues}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1"
                  title={t('admin.columnConfig.actions.viewSampleTooltip', 'Ver los 20 valores únicos más frecuentes para este campo en la última toma del cliente.')}
                >
                  <Eye className="w-3.5 h-3.5" />
                  {t('admin.columnConfig.actions.viewSample', 'Consultar')}
                </button>
              )}
              {showRemove && !isSystem && (
                <button
                  type="button"
                  onClick={() => {
                    const ok = window.confirm(
                      `¿Quitar la columna "${entry.displayName || entry.id}" de la configuración?\n\n` +
                      `• Desaparecerá de la lista de columnas activas para este cliente.\n` +
                      `• Se perderá la configuración guardada (formato, estilo, descripción, orden).\n` +
                      `• La fuente de datos en la base no se toca: si es Native o Tag, podés volver a agregarla desde "Pendientes" más abajo.\n\n` +
                      `Esta acción se aplica recién cuando guardás los cambios.`
                    );
                    if (ok && onRemove) onRemove();
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-red-700 border border-red-200 rounded hover:bg-red-50 flex items-center gap-1"
                  title={t('admin.columnConfig.actions.remove', 'Quitar columna')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('admin.columnConfig.actions.removeLabel', 'Borrar')}
                </button>
              )}
            </div>
          </div>

          {/* Grid 2 columnas: izquierda identificadores + título · derecha formato + estilo + preview */}
          <div className="grid grid-cols-2 gap-4">
            {/* Columna izquierda: identificadores */}
            <div className="space-y-2 min-w-0">
              {/* ID del sistema */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('admin.columnConfig.fields.systemId', 'ID del sistema')}
                </label>
                <div className="px-2 py-1 text-xs font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded truncate">
                  {entry.id}
                </div>
              </div>

              {/* ID de origen (sourceField) — para Tags inferimos del tag_key */}
              {(entry.sourceField !== undefined || sectionType === 'tags') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    {t('admin.columnConfig.fields.sourceField', 'ID de origen')}
                  </label>
                  <div className="px-2 py-1 text-xs font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded truncate">
                    {sectionType === 'tags'
                      ? `tags.tag_value WHERE tag_key='${entry.id}'`
                      : (entry.sourceField || '—')}
                  </div>
                </div>
              )}

              {/* Título de columna (displayName) */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('admin.columnConfig.fields.displayName', 'Título de Columna')}
                </label>
                <input
                  type="text"
                  value={entry.displayName ?? ''}
                  onChange={(e) => onChange({ displayName: e.target.value })}
                  placeholder={entry.id}
                  className="w-1/2 px-2 py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
            </div>

            {/* Columna derecha: formato + estilo + preview */}
            <div className="space-y-2 min-w-0">
              {/* Formato (solo Native/Tags) */}
              {showFormat && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    {t('admin.columnConfig.fields.format', 'Formato')}
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={currentPresetId}
                      onChange={(e) => handlePresetChange(e.target.value)}
                      className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                    >
                      {FORMAT_PRESETS
                        .filter((p) => !p.isCustom && p.id !== 'merged-toma-catalog')
                        .map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                    </select>

                    {(entry.format === 'number' ||
                      entry.format === 'percentage' ||
                      entry.format === 'currency') && (
                      <>
                        <label className="text-xs text-gray-600">
                          {t('admin.columnConfig.fields.decimals', 'Decimales')}:
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={4}
                          value={entry.decimals ?? 0}
                          onChange={(e) =>
                            onChange({
                              decimals: Math.max(0, Math.min(4, parseInt(e.target.value, 10) || 0)),
                            })
                          }
                          className="w-14 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                        />
                      </>
                    )}

                    {entry.format === 'currency' && (
                      <>
                        <label className="text-xs text-gray-600">
                          {t('admin.columnConfig.fields.currencySymbol', 'Símbolo')}:
                        </label>
                        <select
                          value={entry.currencySymbol || '$'}
                          onChange={(e) => onChange({ currencySymbol: e.target.value })}
                          className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                        >
                          <option value="$">$</option>
                          <option value="USD">USD</option>
                          <option value="ARS">ARS</option>
                          <option value="EUR">EUR</option>
                          <option value="BRL">BRL</option>
                        </select>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Estilo */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('admin.columnConfig.fields.style', 'Estilo')}
                </label>
                <select
                  value={entry.styleId || ''}
                  onChange={(e) => onChange({ styleId: e.target.value || undefined })}
                  className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                >
                  <option value="">— sin estilo —</option>
                  {COLUMN_STYLE_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Merge con catalog — Core + Native (no Tags). Guardrail: skip
                  Core columns con renderer dedicado (imagen, titulo, precios, etc) */}
              {(sectionType === 'core' || sectionType === 'native')
                && !isSystem
                && !(sectionType === 'core' && NON_MERGEABLE_CORE_IDS.has(entry.id)) && (
                <div>
                  <label
                    className="block text-xs font-medium text-gray-700 mb-0.5"
                    title={t(
                      'admin.columnConfig.fields.mergeWithCatalogTooltip',
                      'Combina esta columna con un campo del catalog IA. Si catalog tiene valor → muestra catalog; sino → muestra toma. Tooltip al hover indica el origen.'
                    )}
                  >
                    {t('admin.columnConfig.fields.mergeWithCatalog', 'Merge con catalog')}
                  </label>
                  <select
                    value={entry.mergeWithCatalog || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v) {
                        onChange({ mergeWithCatalog: null });
                      } else {
                        onChange({ mergeWithCatalog: v });
                      }
                    }}
                    className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-pink-500 focus:border-pink-500"
                    title={t(
                      'admin.columnConfig.fields.mergeWithCatalogTooltip',
                      'Combina esta columna con un campo del catalog IA. Si catalog tiene valor → muestra catalog; sino → muestra toma. Tooltip al hover indica el origen.'
                    )}
                  >
                    <option value="">
                      {t('admin.columnConfig.fields.mergeWithCatalogNone', 'Sin merge')}
                    </option>
                    {VALID_CATALOG_FIELDS.map((f) => (
                      <option key={f} value={f}>{`catalog.${f}`}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Editable inline — Core + Native, solo si hay catalog field resuelto */}
              {(sectionType === 'core' || sectionType === 'native') && !isSystem && (() => {
                const resolved = (entry.mergeWithCatalog && (VALID_CATALOG_FIELDS as readonly string[]).includes(entry.mergeWithCatalog))
                  ? entry.mergeWithCatalog
                  : (entry.sourceField && (VALID_CATALOG_FIELDS as readonly string[]).includes(entry.sourceField))
                    ? entry.sourceField
                    : null;
                if (!resolved) return null;
                return (
                  <div>
                    <label
                      className="block text-xs font-medium text-gray-700 mb-0.5"
                      title={t(
                        'admin.columnConfig.fields.editableTooltip',
                        'Permite editar inline el valor del catalog. La edición no es sobreescrita por el catalogador IA.'
                      )}
                    >
                      {t('admin.columnConfig.fields.editable', 'Editable inline')}
                    </label>
                    <label className="inline-flex items-center gap-2 px-2 py-1 text-xs border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={!!entry.editable}
                        onChange={(e) => onChange({ editable: e.target.checked })}
                        className="h-3.5 w-3.5"
                      />
                      <span className="text-gray-700">
                        {t('admin.columnConfig.fields.editableLabel', 'Editar {{f}}', { f: `catalog.${resolved}` })}
                      </span>
                    </label>
                  </div>
                );
              })()}

              {/* Preview — debajo de Estilo */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-0.5">
                  {t('admin.columnConfig.fields.preview', 'Preview')}
                </label>
                <div className="px-2 py-1 text-xs border border-gray-200 rounded bg-gray-50">
                  <span
                    className={
                      entry.styleId
                        ? (COLUMN_STYLE_PRESETS.find((p) => p.id === entry.styleId)?.className || '')
                        : 'font-mono text-gray-700'
                    }
                  >
                    {previewText ?? String(pickSample(entry))}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Popover overlay con top 20 valores reales del campo. Aparece sobre
          la card sin alterar el ancho de los campos. Click fuera para cerrar.
          top-3/bottom-3 → la altura del popover queda topeada por la altura
          de la card; la lista interna scrollea si los items no entran. */}
      {showValuesPopover && (
        <>
          <div
            className="absolute inset-0 z-20 bg-white/40"
            onClick={() => setShowValuesPopover(false)}
          />
          <div className="absolute top-3 right-3 bottom-3 z-30 w-80 bg-white border border-indigo-200 rounded-lg shadow-lg p-3 flex flex-col">
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <h4 className="text-xs font-semibold text-indigo-700 whitespace-nowrap">
                {valuesData
                  ? t('admin.columnConfig.sampleValues.titleWithTotal', 'Top 20 Valores (Total: {{n}})', { n: valuesData.totalDistinct.toLocaleString() })
                  : t('admin.columnConfig.sampleValues.title', 'Top 20 Valores')}
              </h4>
              <button
                type="button"
                onClick={() => setShowValuesPopover(false)}
                className="p-0.5 text-gray-400 hover:text-gray-700"
                title={t('common.close', 'Cerrar')}
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto pr-2">
              {valuesLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t('admin.columnConfig.sampleValues.loading', 'Consultando última toma…')}
                </div>
              ) : valuesError ? (
                <p className="text-xs text-red-600">{valuesError}</p>
              ) : valuesData && !valuesData.hasToma ? (
                <p className="text-xs text-gray-600">
                  {t(
                    'admin.columnConfig.sampleValues.noToma',
                    'Este cliente todavía no tiene tomas — no hay datos reales para mostrar.'
                  )}
                </p>
              ) : valuesData ? (
                valuesData.values.length === 0 ? (
                  <p className="text-xs text-gray-600">
                    {t('admin.columnConfig.sampleValues.empty', 'No hay valores poblados.')}
                  </p>
                ) : (
                  <ul className="space-y-0.5 text-xs">
                    {valuesData.values.map((v, i) => {
                      const display = v.value === null || v.value === undefined || v.value === ''
                        ? '<blank>'
                        : String(v.value);
                      return (
                        <li key={i} className="flex justify-between gap-2 py-0.5 border-b border-gray-100 last:border-0">
                          <span className="font-mono truncate text-gray-700">{display}</span>
                          <span className="text-gray-500 tabular-nums flex-shrink-0">{v.count.toLocaleString()}</span>
                        </li>
                      );
                    })}
                  </ul>
                )
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ----- Componente principal -----

const ColumnConfigSection: React.FC<Props> = ({
  sectionType,
  entries,
  candidates = [],
  clientId,
  onChange,
  onReorder,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [entries]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = sortedEntries.map((e) => `${sectionType}-${e.id}`);
    const oldIdx = ids.indexOf(active.id as string);
    const newIdx = ids.indexOf(over.id as string);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(sortedEntries, oldIdx, newIdx);
    onReorder(reordered.map((e) => e.id));
  };

  const hint =
    sectionType === 'core'
      ? t('admin.columnConfig.hints.core')
      : sectionType === 'native'
      ? t('admin.columnConfig.hints.native')
      : t('admin.columnConfig.hints.tags');

  // Filtrar candidatos no configurados
  const pendingCandidates = candidates.filter((c) => !c.alreadyConfigured);

  return (
    <div className="space-y-3">
      {/* Hint */}
      <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
        {hint}
      </p>

      {/* Lista drag-drop */}
      {sortedEntries.length === 0 ? (
        <div className="text-center py-6 text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg">
          {sectionType === 'native'
            ? t('admin.columnConfig.empty.noNativeConfigured', 'No hay columnas nativas configuradas')
            : sectionType === 'tags'
            ? t('admin.columnConfig.empty.noTagsConfigured', 'No hay tags configuradas')
            : t('admin.columnConfig.empty.noColumns', 'Sin columnas')}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedEntries.map((e) => `${sectionType}-${e.id}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {sortedEntries.map((entry) => (
                <ColumnRow
                  key={entry.id}
                  entry={entry}
                  sectionType={sectionType}
                  clientId={clientId}
                  onChange={(updates) => onChange(entry.id, updates)}
                  onRemove={onRemove ? () => onRemove(entry.id) : undefined}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Candidatos pendientes (Native + Tags) */}
      {sectionType !== 'core' && onAdd && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          {pendingCandidates.length === 0 ? (
            <p className="text-xs text-gray-500 italic">
              {sectionType === 'native'
                ? t('admin.columnConfig.empty.noNativeCandidates', 'No hay columnas nativas pendientes de descubrir.')
                : t('admin.columnConfig.empty.noTagCandidates', 'No hay tags pendientes de descubrir.')}
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-orange-700 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" />
                {sectionType === 'native'
                  ? t('admin.columnConfig.candidates.pendingNative', {
                      count: pendingCandidates.length,
                      defaultValue: '{{count}} columnas pendientes de revisión',
                    })
                  : t('admin.columnConfig.candidates.pendingTags', {
                      count: pendingCandidates.length,
                      defaultValue: '{{count}} tags disponibles no configurados aún',
                    })}
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {pendingCandidates.map((cand) => {
                  const isNative = sectionType === 'native';
                  const candNative = cand as NativeCandidate;
                  const candTag = cand as TagCandidate;
                  const id = isNative ? candNative.id : candTag.tagKey;
                  const sample = isNative ? candNative.sample : candTag.sampleValue;
                  const meta = isNative
                    ? `${candNative.sqlType} · ${
                        Math.round((candNative.coverage ?? 0) * 100)
                      }%`
                    : `${candTag.rowCount} filas`;
                  const isReserved = isNative && (candNative as any).reserved === true;
                  return (
                    <div
                      key={id}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs border ${
                        isReserved ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-100'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="font-mono font-semibold text-gray-900">{id}</span>
                        <span className="text-gray-500 ml-2">({meta})</span>
                        {sample !== null && sample !== undefined && sample !== '' && (
                          <span className="text-gray-400 ml-2 truncate">
                            ej: {String(sample).slice(0, 30)}
                          </span>
                        )}
                        {isReserved && (
                          <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-medium bg-amber-200 text-amber-800 rounded uppercase tracking-wide" title="Este ID coincide con una columna del sistema. Al agregar se pedirá un ID alternativo.">
                            ID reservado
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        title={isReserved
                          ? 'ID coincide con una columna del sistema (ej: SKU virtual). Al agregar se te pedirá un ID alternativo (ej: sku_db) para no chocar.'
                          : undefined}
                        onClick={() => {
                          let finalId = id;
                          if (isReserved) {
                            const suggested = `${id}_db`;
                            const userInput = window.prompt(
                              `El ID "${id}" está reservado por una columna del sistema.\n\n` +
                              `Ingresá un ID alternativo (la columna se mostrará con ese nombre interno; el dato sigue viniendo del campo "${id}" de la DB):`,
                              suggested
                            );
                            if (!userInput || !userInput.trim()) return; // cancel
                            finalId = userInput.trim();
                            // Validación básica: no vacío, no idéntico al reservado, no chocar con entries existentes
                            if (finalId === id) {
                              alert('El ID alternativo no puede ser igual al reservado.');
                              return;
                            }
                            if (sortedEntries.some(e => e.id === finalId)) {
                              alert(`Ya existe una entrada con id "${finalId}".`);
                              return;
                            }
                          }
                          const baseEntry: Partial<SectionEntry> = {
                            enabled: false,
                            order: sortedEntries.length,
                            pendingReview: true,
                            displayName: id,
                            sample,
                          };
                          if (isNative) {
                            baseEntry.sourceField = candNative.sourceField;
                            baseEntry.format = candNative.typeHint || 'text';
                          } else {
                            baseEntry.format = 'text';
                          }
                          onAdd(finalId, baseEntry);
                        }}
                        className="px-2 py-1 text-xs font-medium rounded flex items-center gap-1 text-pink-700 bg-white border border-pink-300 hover:bg-pink-50"
                      >
                        <Plus className="w-3 h-3" />
                        {t('admin.columnConfig.actions.addToConfig', 'Agregar')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnConfigSection;
